import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar, CheckCircle, Clock, RefreshCw, XCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { formatCurrency, formatDate } from '@/lib/utils';

interface Subscription {
  id: string;
  user_id: string;
  plan: string;
  created_at: string;
  current_period_end: string | null;
  stripe_customer_id: string | null;
  quota_appointments: number | null;
  quota_staff: number | null;
  updated_at: string;
  // Extended properties for display
  user_email?: string;
  user_name?: string;
  user_role?: string;
}

interface Profile {
  id: string;
  email: string;
  full_name: string | null;
  role: string;
}

// Extending the Subscription interface to include user information
interface SubscriptionWithUser extends Subscription {
  user_email: string;
  user_name: string;
  user_role: string;
}

const AdminSubscriptionsTab = () => {
  const [subscriptions, setSubscriptions] = useState<SubscriptionWithUser[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchSubscriptions = async () => {
    setLoading(true);
    
    try {
      // Fetch subscriptions joined with user profiles
      const { data: subsData, error: subsError } = await supabase
        .from('subscriptions')
        .select('*')
        .order('updated_at', { ascending: false });
      
      if (subsError) throw subsError;
      
      // Fetch user profiles
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, email, full_name, role');
      
      if (profilesError) throw profilesError;
      
      // Create a map for fast user lookups
      const profileMap = new Map();
      if (profilesData && Array.isArray(profilesData)) {
        profilesData.forEach((profile: any) => {
          if (profile && profile.id) {
            profileMap.set(profile.id, {
              email: profile.email || 'Unknown',
              full_name: profile.full_name || 'Unknown User',
              role: profile.role || 'unknown'
            });
          }
        });
      }
      
      // Combine subscription data with user data
      const subscriptionsWithUsers: SubscriptionWithUser[] = subsData && Array.isArray(subsData) 
        ? subsData.map((sub: any) => {
            const profile = sub.user_id && profileMap.get(sub.user_id) || 
                          { email: 'Unknown', full_name: 'Unknown User', role: 'unknown' };
            return {
              ...sub,
              user_email: profile.email,
              user_name: profile.full_name || profile.email.split('@')[0],
              user_role: profile.role
            } as SubscriptionWithUser;
          }) 
        : [];
      
      setSubscriptions(subscriptionsWithUsers);
    } catch (error: any) {
      console.error('Error fetching subscriptions:', error);
      toast({
        title: 'Error fetching subscriptions',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const getPlanColor = (plan: string) => {
    switch (plan) {
      case 'free':
        return 'bg-gray-600';
      case 'pro':
        return 'bg-blue-600';
      case 'studio_free':
        return 'bg-gray-600';
      case 'studio_pro':
        return 'bg-green-600';
      case 'studio_business':
        return 'bg-purple-600';
      case 'studio_premium':
        return 'bg-yellow-600';
      default:
        return 'bg-gray-600';
    }
  };

  const getPlanDisplayName = (plan: string): string => {
    switch (plan) {
      case 'free': return 'Basic (Kostenlos)';
      case 'pro': return 'Premium (1€)';
      case 'studio_free': return 'Studio Free';
      case 'studio_pro': return 'Studio Pro (19€)';
      case 'studio_business': return 'Business (39€)';
      case 'studio_premium': return 'Premium (59€)';
      default: return plan;
    }
  };

  const renderStatus = (s: Subscription | null) => {
    if (!s) return <Badge variant="outline">Unknown</Badge>;
  
    // Check if subscription is active based on current_period_end
    const isActive = s.current_period_end ? new Date(s.current_period_end) > new Date() : true;
    
    if (isActive) {
      return (
        <Badge className="bg-green-600">
          <CheckCircle className="w-3 h-3 mr-1" /> Active
        </Badge>
      );
    } else {
      return (
        <Badge variant="destructive">
          <XCircle className="w-3 h-3 mr-1" /> Expired
        </Badge>
      );
    }
  };

  const renderPlanDetails = (subscription: Subscription | null) => {
    if (!subscription) return null;
    
    return (
      <div className="space-y-1 text-sm">
        <div className="flex items-center">
          <Calendar className="w-4 h-4 mr-2 opacity-70" /> 
          <span>Created: {format(new Date(subscription.created_at), 'PPP')}</span>
        </div>
        {subscription.current_period_end && (
          <div className="flex items-center">
            <Clock className="w-4 h-4 mr-2 opacity-70" /> 
            <span>
              {new Date(subscription.current_period_end) > new Date()
                ? `Renews: ${format(new Date(subscription.current_period_end), 'PPP')}`
                : `Expired: ${format(new Date(subscription.current_period_end), 'PPP')}`}
            </span>
          </div>
        )}
        <div className="flex items-center">
          {subscription.quota_appointments ? (
            <span className="text-xs text-gray-400">Quota: {subscription.quota_appointments} appointments</span>
          ) : (
            <span className="text-xs text-gray-400">Unlimited appointments</span>
          )}
        </div>
        {subscription.user_role && 
          (subscription.user_role === 'studio_owner' || subscription.plan.startsWith('studio_')) && (
          <div className="flex items-center">
            {subscription.quota_staff ? (
              <span className="text-xs text-gray-400">Staff limit: {subscription.quota_staff}</span>
            ) : (
              <span className="text-xs text-gray-400">Unlimited staff</span>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <Card className="border-chrona-gray-800 bg-chrona-gray-900/60 overflow-hidden">
      <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
        <CardTitle className="text-chrona-text">Subscriptions</CardTitle>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={fetchSubscriptions} 
          disabled={loading}
          className="text-chrona-text border-chrona-gray-700"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Refresh
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="flex justify-center items-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-chrona-accent" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-chrona-gray-800 bg-chrona-gray-900/80">
                  <th className="p-3 text-left text-xs font-medium text-chrona-gray-400">User</th>
                  <th className="p-3 text-left text-xs font-medium text-chrona-gray-400">Plan</th>
                  <th className="p-3 text-left text-xs font-medium text-chrona-gray-400">Status</th>
                  <th className="p-3 text-left text-xs font-medium text-chrona-gray-400">Details</th>
                </tr>
              </thead>
              <tbody>
                {subscriptions.length > 0 ? (
                  subscriptions.map((subscription) => (
                    <tr key={subscription.id} className="border-b border-chrona-gray-800 hover:bg-chrona-gray-900/40">
                      <td className="p-3">
                        <div className="font-medium text-chrona-text">{subscription.user_name}</div>
                        <div className="text-sm text-chrona-gray-400">{subscription.user_email}</div>
                        <Badge 
                          variant="outline" 
                          className="mt-1 text-xs"
                        >
                          {subscription.user_role || 'unknown'}
                        </Badge>
                      </td>
                      <td className="p-3">
                        <Badge 
                          className={`${getPlanColor(subscription.plan)} text-white capitalize`}
                        >
                          {getPlanDisplayName(subscription.plan)}
                        </Badge>
                      </td>
                      <td className="p-3">{renderStatus(subscription)}</td>
                      <td className="p-3">{renderPlanDetails(subscription)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="p-4 text-center text-chrona-gray-400">
                      No subscriptions found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminSubscriptionsTab;
